import * as p from '@clack/prompts'
import pc from 'picocolors'

import { runVercel } from './runner.js'
import type { AuthResult, VercelRunner } from './types.js'

const WHOAMI_TIMEOUT_MS = 30_000
/** Login polls a browser approval, so give it a long window. */
const LOGIN_TIMEOUT_MS = 300_000

/**
 * Non-triggering auth check. `vercel whoami` exits 0 + prints the username when
 * authenticated; a non-zero exit means not signed in. Never launches a login.
 */
export async function checkVercelAuth(
  runner: VercelRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<AuthResult> {
  const result = await runVercel(runner, ['whoami'], {
    cwd,
    mode: 'capture',
    timeoutMs: WHOAMI_TIMEOUT_MS,
    env
  })

  if (result.success) {
    const username = result.stdout.trim().split('\n').pop()?.trim()
    return { authed: true, username: username || undefined }
  }

  return { authed: false, reason: result.timedOut ? 'timeout' : 'not-authed' }
}

/**
 * Ensure the user is authenticated with Vercel.
 * - If already signed in, returns immediately.
 * - If `VERCEL_TOKEN` is set but invalid, fails without launching interactive login.
 * - Otherwise runs `vercel login`, which prints a clickable link + verification
 *   code and polls until approved, then re-verifies with `whoami`.
 *
 * Callers must stop any active clack spinner before calling this: `vercel login`
 * inherits the TTY and would fight a running spinner animation.
 */
export async function ensureVercelAuth(
  runner: VercelRunner,
  cwd: string,
  options: { env?: NodeJS.ProcessEnv }
): Promise<AuthResult> {
  const env = options.env ?? process.env

  const existing = await checkVercelAuth(runner, cwd, env)
  if (existing.authed) return existing

  // A token was supplied but whoami failed → token is invalid/expired. Do not
  // drop into an interactive browser login in what may be a headless run.
  if (env.VERCEL_TOKEN) {
    return { authed: false, reason: 'login-failed' }
  }

  p.log.info(
    `Sign in to Vercel to continue — a browser link and code will appear below. ${pc.dim(
      '(or press Ctrl-C to enter a connection string manually)'
    )}`
  )

  const login = await runVercel(runner, ['login'], {
    cwd,
    mode: 'inherit',
    timeoutMs: LOGIN_TIMEOUT_MS,
    env
  })

  if (!login.success) {
    return { authed: false, reason: login.timedOut ? 'timeout' : 'login-cancelled' }
  }

  const after = await checkVercelAuth(runner, cwd, env)
  return after.authed ? after : { authed: false, reason: 'login-failed' }
}
